import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectFields, setFields, paymentMethodSlice,
  selectGooglePay, setGooglePayLoading, setGooglePayInstallments, setGooglePaySelectedInstallment, resetGooglePay
} from '../app/reducers/paymentMethod';
import { useGetWalletInstallmentsMutation } from '../app/services/walletInstallments';

const GOOGLE_PAY_TOKEN_FIELD = 'field_google_pay_token';
const INSTALLMENTS_FIELD = 'field_installments';
const PROVIDER_GOOGLE_PAY = 'GOOGLE_PAY';

export function useGooglePayFlow({ operation, onSubmit }) {
  const dispatch = useDispatch();
  const fields = useSelector(selectFields);
  const paymentMethod = useSelector(paymentMethodSlice.selectSlice);
  const googlePay = useSelector(selectGooglePay);
  const [getWalletInstallments] = useGetWalletInstallmentsMutation();

  const tokenRef = useRef(null);

  const step = (googlePay && googlePay.step) || 'button';
  const cardInfo = googlePay && googlePay.cardInfo;
  const installments = (googlePay && googlePay.installments) || [];
  const installmentsI18n = googlePay && googlePay.i18n;
  const selectedInstallment = googlePay && googlePay.selectedInstallment;

  const buildFields = useCallback((token, installmentOption) => {
    const base = (fields || []).filter(
      (f) => f.name !== GOOGLE_PAY_TOKEN_FIELD && f.name !== INSTALLMENTS_FIELD
    );
    const result = [
      ...base,
      {
        name: GOOGLE_PAY_TOKEN_FIELD,
        value: token,
        is_valid: true,
        timestamp: Date.now(),
        random_key: `${GOOGLE_PAY_TOKEN_FIELD}_${Date.now()}`
      }
    ];
    if (installmentOption && String(installmentOption.installments) !== '0') {
      result.push({
        name: INSTALLMENTS_FIELD,
        value: installmentOption.installments,
        meta: installmentOption,
        is_valid: true,
        timestamp: Date.now(),
        random_key: `${INSTALLMENTS_FIELD}_${Date.now()}`
      });
    }
    return result;
  }, [fields]);

  const submitWith = useCallback((newFields) => {
    dispatch(setFields(newFields));
    const paymentMethodOverride = { ...(paymentMethod || {}), fields: newFields };
    if (typeof onSubmit === 'function') {
      onSubmit(paymentMethodOverride);
    }
    dispatch(resetGooglePay());
  }, [dispatch, onSubmit, paymentMethod]);

  const onToken = useCallback(async (token, info) => {
    tokenRef.current = token;

    dispatch(setGooglePayLoading({ cardInfo: info || null }));

    try {
      const response = await getWalletInstallments({
        operation,
        provider: PROVIDER_GOOGLE_PAY,
        token
      }).unwrap();

      const installmentsList = (response && response.installments) || [];
      const hasRealInstallments = installmentsList.some(
        (opt) => opt && String(opt.installments) !== '0'
      );

      if (!hasRealInstallments) {
        submitWith(buildFields(token, null));
        return;
      }

      dispatch(setGooglePayInstallments({
        cardInfo: info || null,
        installments: installmentsList,
        i18n: (response && response.i18n) || null
      }));
    } catch (e) {
      console.error('useGooglePayFlow getWalletInstallments error', e);
      dispatch(resetGooglePay());
    }
  }, [operation, getWalletInstallments, submitWith, buildFields, dispatch]);

  const setSelectedInstallment = useCallback((opt) => {
    dispatch(setGooglePaySelectedInstallment(opt));
  }, [dispatch]);

  const confirmInstallments = useCallback(() => {
    const choice = selectedInstallment || (installments.length > 0 ? installments[0] : null);
    submitWith(buildFields(tokenRef.current, choice));
  }, [selectedInstallment, installments, submitWith, buildFields]);

  const reset = useCallback(() => {
    dispatch(resetGooglePay());
  }, [dispatch]);

  return {
    step,
    cardInfo,
    installments,
    installmentsI18n,
    selectedInstallment,
    setSelectedInstallment,
    onToken,
    confirmInstallments,
    reset
  };
}
